import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import IssueReporter from './issues/IssueReporter';
import OfficeReceptionAlert from './OfficeReceptionAlert';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import {
  AppBar,
  Box,
  Drawer,
  CssBaseline,
  Divider,
  FormControl,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  Dialog,
} from '@mui/material';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import BuildCircleOutlinedIcon from '@mui/icons-material/BuildCircleOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import { useTheme } from '@mui/material/styles';
import { collection, getDocsFromServer, getFirestore, onSnapshot, orderBy, query } from 'firebase/firestore';

// ─── Design Tokens (identical to DashboardLayout) ────────────────────────────
const GOLD            = '#9d6f32';
const DARK            = '#1a1a1a';
const MUTED           = '#6b6b6b';
const BORDER          = '#e8e8e8';
const SIDEBAR_BG      = '#fafaf9';
const SIDEBAR_TEXT    = '#6b6b6b';
const SIDEBAR_DIVIDER = '#e8e8e8';
const SIDEBAR_HOVER   = 'rgba(0,0,0,0.04)';
const SIDEBAR_SEL_BG  = 'rgba(157,111,50,0.08)';
const FONT_HEADING    = "'Georgia', 'Times New Roman', serif";
const FONT_BODY       = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
const LOGO_URL        = '/logo192.png';
// ─────────────────────────────────────────────────────────────────────────────

const APP_MODE_STORAGE_KEY = 'swarna_app_mode';
const OFFICE_PROJECT_PICKER_ON_ENTER_KEY = 'office:showProjectPickerOnEnter';
const APP_MODE_PICKER_SKIP_ONCE_KEY = 'swarna_app_mode_picker_skip_once';
const APP_MODE_SWITCH_BYPASS_KEY = 'swarna_app_mode_switch_bypass_once';
type AppMode = 'crm' | 'office';

export default function OfficeLayout() {
  const { currentUser, roleId, projectIds, officeProjectIds, currentProjectId, setCurrentProjectId } = useAuth();
  const { canPage } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const appBarRef = useRef<HTMLDivElement | null>(null);
  const isAdmin = String(roleId || '') === 'super-admin' || String(roleId || '') === 'admin';
  const appMode: AppMode = location.pathname.startsWith('/office') ? 'office' : 'crm';
  const isOfficeMessagesPage = location.pathname.startsWith('/office/messages');
  const isOfficeUnitDetailPage = /^\/office\/units\/[^/]+/.test(location.pathname);
  const db = useMemo(() => getFirestore(), []);
  const shouldOpenProjectPickerOnMount =
    typeof window !== 'undefined' &&
    window.localStorage.getItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY) === '1';
  const [projects, setProjects] = useState<any[]>([]);
  const [modePickerOpen, setModePickerOpen] = useState(false);
  const [modePickerChecked, setModePickerChecked] = useState(shouldOpenProjectPickerOnMount);
  const [projectPickerOpen, setProjectPickerOpen] = useState(shouldOpenProjectPickerOnMount);
  const [projectPickerSelection, setProjectPickerSelection] = useState<string>('');
  const [officeNavCollapsed, setOfficeNavCollapsed] = useState(true);
  const [officeMobileMenuOpen, setOfficeMobileMenuOpen] = useState(false);

  useEffect(() => {
    setOfficeNavCollapsed(true);
    setOfficeMobileMenuOpen(false);
  }, [location.pathname]);

  // Track AppBar height via CSS variable for content offset
  useEffect(() => {
    const el = appBarRef.current;
    if (!el) return;
    const setVar = () => {
      const h = el.offsetHeight || 0;
      document.documentElement.style.setProperty('--office-appbar-height', `${h}px`);
    };
    setVar();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', setVar);
      return () => window.removeEventListener('resize', setVar);
    }
    const ro = new ResizeObserver(() => setVar());
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const projectsQuery = query(collection(db, 'projects'), orderBy('createdAt', 'desc'));
    let active = true;
    let receivedServerSnapshot = false;
    const applyProjects = (snap: any) => {
      const next = snap.docs.map((d: any) => {
        const v = d.data() as any;
        return {
          id: d.id,
          name: typeof v?.name === 'string' ? v.name : null,
          logoUrl: typeof v?.logoUrl === 'string' ? v.logoUrl : null,
          unitTypes: Array.isArray(v?.unitTypes) ? v.unitTypes : null,
          createdAt: v?.createdAt ?? null,
        };
      });
      setProjects((prev) => {
        if (!Array.isArray(prev) || prev.length !== next.length) return next as any;
        for (let i = 0; i < next.length; i += 1) {
          const a: any = prev[i];
          const b: any = next[i];
          if (!a || !b) return next as any;
          if (String(a.id) !== String(b.id)) return next as any;
          if (String(a.name || '') !== String(b.name || '')) return next as any;
          if (String(a.logoUrl || '') !== String(b.logoUrl || '')) return next as any;
          const aU = Array.isArray(a.unitTypes) ? a.unitTypes : [];
          const bU = Array.isArray(b.unitTypes) ? b.unitTypes : [];
          if (aU.length !== bU.length) return next as any;
          for (let j = 0; j < aU.length; j += 1) {
            if (String(aU[j] || '') !== String(bU[j] || '')) return next as any;
          }
        }
        return prev;
      });
    };
    const unsub = onSnapshot(
      projectsQuery,
      { includeMetadataChanges: true },
      (snap) => {
        if (snap.metadata.fromCache && !receivedServerSnapshot) return;
        if (!snap.metadata.fromCache) receivedServerSnapshot = true;
        applyProjects(snap);
      },
      () => { /* Keep previous on transient errors */ }
    );
    getDocsFromServer(projectsQuery)
      .then((snap) => {
        if (!active) return;
        receivedServerSnapshot = true;
        applyProjects(snap);
      })
      .catch(() => { /* The realtime listener handles later recovery. */ });
    return () => {
      active = false;
      unsub();
    };
  }, [db]);

  const visibleProjects = useMemo(() => {
    const accessProjectIds = officeProjectIds.length ? officeProjectIds : projectIds;
    if (isAdmin && (!accessProjectIds || accessProjectIds.length === 0)) return projects;
    const allowed = new Set((accessProjectIds || []).filter(Boolean));
    return projects.filter((p) => allowed.has(p.id));
  }, [isAdmin, officeProjectIds, projectIds, projects]);

  const hasCrmAccess = canPage('dashboard') || canPage('leads') || canPage('followUps') || canPage('brokers');
  const hasOfficeAccess = isAdmin || canPage('office') || canPage('officeMaterialOrders') || canPage('officePayroll') || canPage('officeUserManagement') || canPage('officeSops');

  const projectSelectValue = useMemo(() => {
    if (visibleProjects.length === 0) return '';
    if (currentProjectId && visibleProjects.some((p) => p.id === currentProjectId)) return currentProjectId;
    return '';
  }, [currentProjectId, visibleProjects]);

  useEffect(() => {
    if (!currentUser) return;
    if (visibleProjects.length === 0) return;
    const ok = currentProjectId && visibleProjects.some((p) => p.id === currentProjectId);
    if (!ok) setCurrentProjectId(visibleProjects[0].id);
  }, [currentProjectId, currentUser, setCurrentProjectId, visibleProjects]);

  useLayoutEffect(() => {
    if (!currentUser) return;
    if (!location.pathname.startsWith('/office')) return;
    const shouldShow = window.localStorage.getItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY) === '1';
    if (!shouldShow) return;
    window.localStorage.removeItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY);
    setModePickerChecked(true);
    setModePickerOpen(false);
    const initialSelection =
      (currentProjectId && visibleProjects.some((p) => p.id === currentProjectId)
        ? currentProjectId
        : visibleProjects[0]?.id) || '';
    setProjectPickerSelection(String(initialSelection || ''));
    setProjectPickerOpen(true);
  }, [currentProjectId, currentUser, location.pathname, visibleProjects]);

  useLayoutEffect(() => {
    if (!currentUser) { setModePickerChecked(false); setModePickerOpen(false); return; }
    if (!location.pathname.startsWith('/office')) return;
    if (projectPickerOpen) { setModePickerOpen(false); setModePickerChecked(true); return; }
    const storedMode = window.localStorage.getItem(APP_MODE_STORAGE_KEY);
    if (storedMode === 'office') { setModePickerOpen(false); setModePickerChecked(true); return; }
    if (modePickerChecked) return;
    if (!(hasCrmAccess && hasOfficeAccess)) { setModePickerOpen(false); setModePickerChecked(true); return; }
    const switched = window.localStorage.getItem(APP_MODE_SWITCH_BYPASS_KEY) === '1';
    if (switched) { window.localStorage.removeItem(APP_MODE_SWITCH_BYPASS_KEY); setModePickerOpen(false); setModePickerChecked(true); return; }
    const shouldShowProjectPicker = window.localStorage.getItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY) === '1';
    if (shouldShowProjectPicker) { setModePickerOpen(false); setModePickerChecked(true); return; }
    const skipOnce = window.localStorage.getItem(APP_MODE_PICKER_SKIP_ONCE_KEY) === '1';
    if (skipOnce) { window.localStorage.removeItem(APP_MODE_PICKER_SKIP_ONCE_KEY); setModePickerOpen(false); setModePickerChecked(true); return; }
    setModePickerOpen(true);
    setModePickerChecked(true);
  }, [currentUser, hasCrmAccess, hasOfficeAccess, location.pathname, modePickerChecked, projectPickerOpen]);

  const handleLogout = () => signOut(auth);

  const officeNavItems = useMemo(() => {
    const items: Array<{ key: string; label: string; path: string; icon: React.ReactNode; visible: boolean }> = [
      { key: 'home',          label: 'Today',         path: '/office/today',          icon: <HomeOutlinedIcon />,            visible: isAdmin },
      { key: 'units',         label: 'Units',         path: '/office/units',          icon: <ApartmentOutlinedIcon />,       visible: isAdmin },
      { key: 'banks',         label: 'Banks',         path: '/office/banks',          icon: <AccountBalanceOutlinedIcon />,  visible: isAdmin },
      { key: 'reconciliation',label: 'Reconciliation',path: '/office/reconciliation', icon: <ReceiptLongOutlinedIcon />,     visible: isAdmin },
      { key: 'changeOrders',  label: 'Change Orders', path: '/office/change-orders',  icon: <BuildCircleOutlinedIcon />,     visible: isAdmin },
      { key: 'reports',       label: 'Reports',       path: '/office/reports',        icon: <AssessmentOutlinedIcon />,      visible: isAdmin },
      { key: 'messages',      label: 'Messages',      path: '/office/messages',       icon: <ChatBubbleOutlineIcon />,       visible: isAdmin && !!canPage('messages') },
      { key: 'estimation',    label: 'Estimation',    path: '/office/estimation',     icon: <CalculateOutlinedIcon />,       visible: isAdmin },
      { key: 'materialOrders',label: 'Orders & Receipts', path: '/office/material-orders', icon: <ReceiptLongOutlinedIcon />, visible: isAdmin || !!canPage('officeMaterialOrders') },
      { key: 'payroll',       label: 'Payroll',       path: '/office/payroll',         icon: <PaymentsOutlinedIcon />,        visible: isAdmin || !!canPage('officePayroll') },
      { key: 'sops',          label: 'Agency SOPs',   path: '/office/sops',            icon: <FactCheckOutlinedIcon />,       visible: isAdmin || !!canPage('officeSops') },
      { key: 'requestForms',  label: 'Request Forms', path: '/office/request-forms',  icon: <DescriptionOutlinedIcon />,     visible: false },
    ];
    return items.filter((x) => x.visible);
  }, [canPage, isAdmin]);

  const officeUtilityItems = useMemo(() => {
    const items: Array<{ key: string; label: string; path: string; icon: React.ReactNode; visible: boolean }> = [
      { key: 'feasibility', label: 'Feasibility', path: '/office/feasibility', icon: <RequestQuoteOutlinedIcon />, visible: isAdmin },
      { key: 'contactBook', label: 'Contact Book', path: '/office/contact-book', icon: <ContactsOutlinedIcon />, visible: isAdmin && !!canPage('settings') },
      { key: 'issues',      label: 'Reported issues', path: '/office/issues',   icon: <FlagOutlinedIcon />,         visible: String(currentUser?.email || '').toLowerCase() === 'shetajatin18@gmail.com' },
      { key: 'settings',    label: 'Settings',    path: '/office/settings',    icon: <SettingsOutlinedIcon />,     visible: isAdmin && !!canPage('settings') },
    ];
    return items.filter((x) => x.visible);
  }, [canPage, currentUser?.email, isAdmin]);

  const selectedOfficeNavKey = useMemo(() => {
    const p = location.pathname || '';
    if (p.startsWith('/office/units'))          return 'units';
    if (p.startsWith('/office/estimation'))     return 'estimation';
    if (p.startsWith('/office/material-orders')) return 'materialOrders';
    if (p.startsWith('/office/payroll'))        return 'payroll';
    if (p.startsWith('/office/sops'))           return 'sops';
    if (p.startsWith('/office/user-management')) return 'settings';
    if (p.startsWith('/office/feasibility'))    return 'feasibility';
    if (p.startsWith('/office/contact-book'))   return 'contactBook';
    if (p.startsWith('/office/banks'))          return 'banks';
    if (p.startsWith('/office/change-orders'))  return 'changeOrders';
    if (p.startsWith('/office/reports'))        return 'reports';
    if (p.startsWith('/office/messages'))       return 'messages';
    if (p.startsWith('/office/reconciliation')) return 'reconciliation';
    if (p.startsWith('/office/request-forms'))  return 'requestForms';
    if (p.startsWith('/office/issues'))         return 'issues';
    if (p.startsWith('/office/settings'))       return new URLSearchParams(location.search || '').get('section') === 'contactsBook' ? 'contactBook' : 'settings';
    return 'home';
  }, [location.pathname, location.search]);


  // ─── Mode toggle pill (shared by AppBar desktop + mobile drawer) ───────────
  const modePill = (onNavigate?: () => void) => (
    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f4f4f4', border: `1px solid ${BORDER}`, borderRadius: 999, p: 0.4 }}>
      {(['crm', 'office'] as AppMode[]).map((mode) => (
        <Box
          key={mode}
          onClick={() => {
            window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1');
            window.localStorage.setItem(APP_MODE_SWITCH_BYPASS_KEY, '1');
            localStorage.setItem(APP_MODE_STORAGE_KEY, mode);
            if (mode === 'office') localStorage.setItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY, '1');
            onNavigate?.();
            navigate(mode === 'office' ? '/office' : '/', { replace: true });
          }}
          sx={{
            px: 2, py: 0.625, borderRadius: 999, cursor: 'pointer',
            fontFamily: FONT_BODY, fontWeight: 700, fontSize: 12,
            textTransform: 'uppercase', letterSpacing: '0.06em',
            bgcolor: appMode === mode ? GOLD : 'transparent',
            color: appMode === mode ? '#fff' : MUTED,
            transition: 'all 0.15s ease', userSelect: 'none',
            '&:hover': appMode !== mode ? { color: DARK } : {},
          }}
        >
          {mode.toUpperCase()}
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <CssBaseline />

      {/* ── AppBar ─────────────────────────────────────────────────────────── */}
      <AppBar
        ref={appBarRef}
        component="nav"
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          bgcolor: '#fff',
          color: DARK,
          borderBottom: `2px solid ${GOLD}`,
          boxShadow: '0 1px 8px rgba(0,0,0,0.06)',
        }}
      >
        <Toolbar sx={{ gap: 1 }}>
          {/* Hamburger */}
          <IconButton
            edge="start"
            onClick={() => isMobile ? setOfficeMobileMenuOpen(true) : setOfficeNavCollapsed((prev) => !prev)}
            aria-label={isMobile ? 'Open menu' : officeNavCollapsed ? 'Expand menu' : 'Collapse menu'}
            sx={{ color: DARK, mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>

          {/* Brand logo + label */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, overflow: 'hidden' }}>
            <Box component="img" src={LOGO_URL} alt="Swarna Group" sx={{ height: { xs: 28, sm: 32 }, width: 'auto', flexShrink: 0, display: 'block' }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: { xs: 14, sm: 16 }, color: DARK, lineHeight: 1.1, whiteSpace: 'nowrap', userSelect: 'none' }}>
                Swarna Group
              </Typography>
              <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 600, fontSize: { xs: 9, sm: 10 }, color: MUTED, letterSpacing: '0.1em', textTransform: 'uppercase', lineHeight: 1, userSelect: 'none' }}>
                Office
              </Typography>
            </Box>
          </Box>

          {/* Report a problem: on every Office page, all screen sizes. */}
          <IssueReporter projectName={visibleProjects.find((x: any) => x.id === projectSelectValue)?.name || null} />
          {/* Desktop: toggle + project + settings + logout */}
          <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
            {isAdmin && modePill()}

            {/* Project selector */}
            <FormControl size="small" sx={{ minWidth: 160, maxWidth: 240 }}>
              <Select
                value={projectSelectValue}
                onChange={(e) => setCurrentProjectId(e.target.value as string)}
                displayEmpty
                disabled={visibleProjects.length <= 1}
                sx={{
                  color: DARK, fontFamily: FONT_BODY, fontSize: 13,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bbb' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                  '& .MuiSvgIcon-root': { color: MUTED },
                  '& .MuiSelect-select': { py: 0.875 },
                }}
                renderValue={(val) => {
                  const id = val as string;
                  if (!id) return 'Select Project';
                  const p = visibleProjects.find((x) => x.id === id);
                  return p?.name || 'Select Project';
                }}
              >
                {visibleProjects.length === 0 ? (
                  <MenuItem disabled value="">No projects</MenuItem>
                ) : (
                  visibleProjects.slice().sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''))).map((p: any) => (
                    <MenuItem key={p.id} value={p.id} sx={{ fontFamily: FONT_BODY, fontSize: 13 }}>{p.name}</MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

          </Box>
        </Toolbar>
      </AppBar>
      {/* ─────────────────────────────────────────────────────────────────── */}

      {/* Mobile hamburger drawer */}
      <Drawer
        anchor="left"
        open={officeMobileMenuOpen}
        onClose={() => setOfficeMobileMenuOpen(false)}
        ModalProps={{
          keepMounted: true,
          BackdropProps: {
            sx: { top: 'var(--office-appbar-height, 64px)' },
          },
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          zIndex: 1300,
          '& .MuiDrawer-paper': {
            width: 300,
            boxSizing: 'border-box',
            bgcolor: SIDEBAR_BG,
            top: 'var(--office-appbar-height, 64px)',
            height: 'calc(100% - var(--office-appbar-height, 64px))',
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: SIDEBAR_BG }}>

          {/* Nav items — scrollable */}
          <List sx={{ py: 1, px: 0.75, flex: 1, overflowY: 'auto' }}>
            {officeNavItems.map((item) => {
              const isSelected = item.key === selectedOfficeNavKey;
              return (
                <ListItemButton
                  key={item.key}
                  selected={isSelected}
                  onClick={() => { setOfficeMobileMenuOpen(false); navigate(item.path); }}
                  sx={{
                    minHeight: 48, borderRadius: 1.5, mb: 0.25,
                    borderLeft: isSelected ? `3px solid ${GOLD}` : '3px solid transparent',
                    color: isSelected ? GOLD : SIDEBAR_TEXT,
                    bgcolor: isSelected ? SIDEBAR_SEL_BG : 'transparent',
                    '&.Mui-selected': { bgcolor: SIDEBAR_SEL_BG, color: GOLD, '&:hover': { bgcolor: 'rgba(157,111,50,0.12)' } },
                    '&:hover': { bgcolor: isSelected ? SIDEBAR_SEL_BG : SIDEBAR_HOVER, color: isSelected ? GOLD : DARK },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontFamily: FONT_BODY, fontWeight: isSelected ? 700 : 500, fontSize: 13.5, color: 'inherit' }} />
                </ListItemButton>
              );
            })}
          </List>

          {/* Bottom: project + switch app + logout — pinned */}
          <Box>
            <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />

            {/* Project selector */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
                Project
              </Typography>
              <FormControl size="small" fullWidth>
                <Select
                  value={projectSelectValue}
                  onChange={(e) => setCurrentProjectId(e.target.value as string)}
                  displayEmpty
                  disabled={visibleProjects.length <= 1}
                  sx={{
                    color: DARK, fontFamily: FONT_BODY, fontSize: 13,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#bbb' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
                    '& .MuiSvgIcon-root': { color: MUTED },
                    '& .MuiSelect-select': { py: 1 },
                  }}
                  renderValue={(val) => {
                    const id = val as string;
                    if (!id) return 'Select Project';
                    const p = visibleProjects.find((x) => x.id === id);
                    return p?.name || 'Select Project';
                  }}
                >
                  {visibleProjects.length === 0 ? (
                    <MenuItem disabled value="">No projects</MenuItem>
                  ) : (
                    visibleProjects.slice().sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''))).map((p: any) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            {/* Switch App pill */}
            {isAdmin && (
              <Box sx={{ px: 2, pb: 1.5 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 10, fontWeight: 700, color: MUTED, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
                  Switch App
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f0f0f0', border: `1px solid ${BORDER}`, borderRadius: 999, p: 0.4, width: 'fit-content' }}>
                  {(['crm', 'office'] as AppMode[]).map((mode) => (
                    <Box
                      key={mode}
                      onClick={() => {
                        window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1');
                        window.localStorage.setItem(APP_MODE_SWITCH_BYPASS_KEY, '1');
                        localStorage.setItem(APP_MODE_STORAGE_KEY, mode);
                        if (mode === 'office') localStorage.setItem(OFFICE_PROJECT_PICKER_ON_ENTER_KEY, '1');
                        setOfficeMobileMenuOpen(false);
                        navigate(mode === 'office' ? '/office' : '/', { replace: true });
                      }}
                      sx={{
                        px: 2.5, py: 0.75, borderRadius: 999, cursor: 'pointer',
                        fontFamily: FONT_BODY, fontWeight: 700, fontSize: 13,
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        bgcolor: appMode === mode ? GOLD : 'transparent',
                        color: appMode === mode ? '#fff' : MUTED,
                        transition: 'all 0.15s ease', userSelect: 'none',
                        '&:hover': appMode !== mode ? { color: DARK } : {},
                      }}
                    >
                      {mode.toUpperCase()}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />

            {officeUtilityItems.length > 0 && (
              <Box sx={{ px: 1.5, py: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  {officeUtilityItems.map((item) => {
                    const isSelected = item.key === selectedOfficeNavKey;
                    const isIconOnlyUtility = item.key === 'feasibility' || item.key === 'contactBook';
                    const content = isIconOnlyUtility ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                        {item.icon}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                        <Box sx={{ display: 'flex', fontSize: 18 }}>{item.icon}</Box>
                        <Box component="span">{item.label}</Box>
                      </Box>
                    );
                    return (
                      <Tooltip key={item.key} title={isIconOnlyUtility ? item.label : ''} placement="top">
                      <Box
                        onClick={() => { setOfficeMobileMenuOpen(false); navigate(item.path); }}
                        sx={{
                          border: `1px solid ${isSelected ? GOLD : BORDER}`,
                          bgcolor: isSelected ? SIDEBAR_SEL_BG : '#fff',
                          color: isSelected ? GOLD : SIDEBAR_TEXT,
                          borderRadius: 1.5,
                          width: isIconOnlyUtility ? 62 : 'auto',
                          flex: isIconOnlyUtility ? '0 0 62px' : 1,
                          minWidth: item.key === 'settings' ? 160 : undefined,
                          px: 1,
                          py: 0.9,
                          textAlign: 'center',
                          fontFamily: FONT_BODY,
                          fontSize: 12.5,
                          fontWeight: isSelected ? 700 : 600,
                          cursor: 'pointer',
                          '&:hover': { color: GOLD, borderColor: GOLD, bgcolor: 'rgba(157,111,50,0.08)' },
                        }}
                      >
                        {content}
                      </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Logout */}
            <List sx={{ py: 0.5, px: 0.75 }}>
              <ListItemButton
                onClick={() => { setOfficeMobileMenuOpen(false); handleLogout(); }}
                sx={{ minHeight: 48, borderRadius: 1.5, color: SIDEBAR_TEXT, borderLeft: '3px solid transparent', '&:hover': { bgcolor: 'rgba(220,60,60,0.08)', color: '#d32f2f' } }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: 'inherit' }}><LogoutOutlinedIcon /></ListItemIcon>
                <ListItemText primary="Logout" primaryTypographyProps={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: 13.5 }} />
              </ListItemButton>
            </List>
          </Box>
        </Box>
      </Drawer>

      {/* ── Content area (below fixed AppBar) ─────────────────────────────── */}
      <Box
        sx={{
          flex: '0 0 auto',
          minHeight: 0,
          height: 'calc(100vh - var(--office-appbar-height, 64px))',
          display: 'flex',
          mt: 'var(--office-appbar-height, 64px)',
        }}
      >

        {/* Desktop left sidebar */}
        {!isMobile && (
          <Box
            component="nav"
            sx={{
              width: officeNavCollapsed ? 72 : 240,
              flexShrink: 0,
              borderRight: `1px solid ${BORDER}`,
              bgcolor: SIDEBAR_BG,
              transition: 'width 0.18s ease',
              overflowX: 'hidden',
              position: 'sticky',
              top: 'var(--office-appbar-height, 64px)',
              alignSelf: 'flex-start',
              height: 'calc(100vh - var(--office-appbar-height, 64px))',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <List dense sx={{ px: 0.75, flex: 1, pt: 1 }}>
              {officeNavItems.map((item) => {
                const isSelected = item.key === selectedOfficeNavKey;
                return (
                  <Tooltip key={item.key} title={officeNavCollapsed ? item.label : ''} placement="right">
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => { setOfficeNavCollapsed(true); navigate(item.path); }}
                      sx={{
                        borderRadius: 1.5,
                        mb: 0.25,
                        minHeight: 44,
                        justifyContent: officeNavCollapsed ? 'center' : 'flex-start',
                        px: officeNavCollapsed ? 1 : 1.5,
                        borderLeft: isSelected ? `3px solid ${GOLD}` : '3px solid transparent',
                        color: isSelected ? GOLD : SIDEBAR_TEXT,
                        bgcolor: isSelected ? SIDEBAR_SEL_BG : 'transparent',
                        '&.Mui-selected': {
                          bgcolor: SIDEBAR_SEL_BG,
                          color: GOLD,
                          '&:hover': { bgcolor: 'rgba(157,111,50,0.12)' },
                        },
                        '&:hover': {
                          bgcolor: isSelected ? SIDEBAR_SEL_BG : SIDEBAR_HOVER,
                          color: isSelected ? GOLD : DARK,
                        },
                        transition: 'all 0.15s ease',
                      }}
                    >
                      <ListItemIcon sx={{
                        minWidth: officeNavCollapsed ? 0 : 36,
                        mr: officeNavCollapsed ? 0 : 1.25,
                        color: 'inherit',
                        justifyContent: 'center',
                      }}>
                        {item.icon}
                      </ListItemIcon>
                      {!officeNavCollapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{
                            fontFamily: FONT_BODY,
                            fontWeight: isSelected ? 700 : 500,
                            fontSize: 13.5,
                            color: 'inherit',
                          }}
                        />
                      )}
                    </ListItemButton>
                  </Tooltip>
                );
              })}
            </List>

            {/* Bottom: logout */}
            <Box>
              <Divider sx={{ borderColor: SIDEBAR_DIVIDER }} />
              {officeUtilityItems.length > 0 && (
                <Box sx={{ px: officeNavCollapsed ? 0.75 : 1, py: 0.75 }}>
                  {officeNavCollapsed ? (
                    <Stack spacing={0.5} alignItems="center">
                      {officeUtilityItems.map((item) => {
                        const isSelected = item.key === selectedOfficeNavKey;
                        return (
                          <Tooltip key={item.key} title={item.label} placement="right">
                            <IconButton
                              size="small"
                              onClick={() => { setOfficeNavCollapsed(true); navigate(item.path); }}
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 1.5,
                                color: isSelected ? GOLD : SIDEBAR_TEXT,
                                bgcolor: isSelected ? SIDEBAR_SEL_BG : 'transparent',
                                '&:hover': { bgcolor: SIDEBAR_HOVER, color: GOLD },
                              }}
                            >
                              {item.icon}
                            </IconButton>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                      {officeUtilityItems.map((item) => {
                        const isSelected = item.key === selectedOfficeNavKey;
                        const isIconOnlyUtility = item.key === 'feasibility' || item.key === 'contactBook';
                        const content = isIconOnlyUtility ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>
                            {item.icon}
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                            <Box sx={{ display: 'flex', fontSize: 18 }}>{item.icon}</Box>
                            <Box component="span">{item.label}</Box>
                          </Box>
                        );
                        return (
                          <Tooltip key={item.key} title={isIconOnlyUtility ? item.label : ''} placement="top">
                          <Box
                            onClick={() => { setOfficeNavCollapsed(true); navigate(item.path); }}
                            sx={{
                              border: `1px solid ${isSelected ? GOLD : BORDER}`,
                              bgcolor: isSelected ? SIDEBAR_SEL_BG : '#fff',
                              color: isSelected ? GOLD : SIDEBAR_TEXT,
                              borderRadius: 1.5,
                              width: isIconOnlyUtility ? 54 : 'auto',
                              flex: isIconOnlyUtility ? '0 0 54px' : 1,
                              px: 0.75,
                              py: 0.8,
                              textAlign: 'center',
                              fontFamily: FONT_BODY,
                              fontSize: 12,
                              fontWeight: isSelected ? 700 : 600,
                              cursor: 'pointer',
                              '&:hover': { color: GOLD, borderColor: GOLD, bgcolor: 'rgba(157,111,50,0.08)' },
                            }}
                          >
                            {content}
                          </Box>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  )}
                </Box>
              )}
              <List dense sx={{ px: 0.75, py: 0.75 }}>
                <Tooltip title={officeNavCollapsed ? 'Logout' : ''} placement="right">
                  <ListItemButton
                    onClick={handleLogout}
                    sx={{
                      borderRadius: 1.5, minHeight: 44,
                      justifyContent: officeNavCollapsed ? 'center' : 'flex-start',
                      px: officeNavCollapsed ? 1 : 1.5,
                      color: SIDEBAR_TEXT,
                      borderLeft: '3px solid transparent',
                      '&:hover': { bgcolor: 'rgba(220,60,60,0.08)', color: '#d32f2f' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: officeNavCollapsed ? 0 : 36, mr: officeNavCollapsed ? 0 : 1.25, color: 'inherit', justifyContent: 'center' }}>
                      <LogoutOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    {!officeNavCollapsed && (
                      <ListItemText primary="Logout" primaryTypographyProps={{ fontFamily: FONT_BODY, fontWeight: 500, fontSize: 13.5 }} />
                    )}
                  </ListItemButton>
                </Tooltip>
              </List>
            </Box>
          </Box>
        )}

        {/* Page content */}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            overflowY: isOfficeMessagesPage || isOfficeUnitDetailPage ? 'hidden' : 'auto',
            p: 0,
          }}
        >
          <Box
            id="office-drawer-container"
            sx={{
              position: 'relative',
              minHeight: '100%',
              height: isOfficeMessagesPage || isOfficeUnitDetailPage ? '100%' : 'auto',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 0,
                boxShadow: 'none',
                minHeight: '100%',
                height: isOfficeMessagesPage || isOfficeUnitDetailPage ? '100%' : 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Outlet />
            </Paper>
          </Box>
        </Box>
      </Box>

      {isAdmin && appMode === 'office' && !modePickerOpen && !projectPickerOpen && <OfficeReceptionAlert />}

      {/* ── Project picker fullscreen dialog ───────────────────────────────── */}
      <Dialog open={projectPickerOpen} fullScreen PaperProps={{ sx: { background: 'linear-gradient(160deg, #0a1f5c 0%, #0d3080 50%, #0a1f5c 100%)', color: '#fff', overflowY: 'auto' } }}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', px: { xs: 2, sm: 4 }, py: 3 }}>

          {/* Top bar */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => { setProjectPickerOpen(false); navigate('/', { replace: true }); }}
              sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' }, mr: 1 }}
            >
              <ArrowBackOutlinedIcon />
            </IconButton>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>
              Back to CRM
            </Typography>
          </Box>

          {/* Hero */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: { xs: 5, sm: 8 }, mb: { xs: 5, sm: 7 } }}>
            <Box component="img" src={LOGO_URL} alt="Swarna Group"
              sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 }, objectFit: 'contain', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.12)', p: 1, mb: 2.5 }}
            />
            <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: { xs: 22, sm: 28 }, color: '#fff', mb: 0.75, textAlign: 'center' }}>
              Select a Project
            </Typography>
            <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: 'rgba(255,255,255,0.55)', textAlign: 'center' }}>
              Choose the project you want to work in
            </Typography>
          </Box>

          {/* Project cards */}
          <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {visibleProjects.length === 0 ? (
              <Typography sx={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>No projects available.</Typography>
            ) : (
              visibleProjects.slice().sort((a: any, b: any) => String(a?.name || '').localeCompare(String(b?.name || ''))).map((project: any) => {
                const selected = String(projectPickerSelection || '') === String(project.id || '');
                return (
                  <Box
                    key={String(project.id)}
                    onClick={() => {
                      const nextProjectId = String(project.id || '');
                      setProjectPickerSelection(nextProjectId);
                      if (nextProjectId) setCurrentProjectId(nextProjectId);
                      setProjectPickerOpen(false);
                    }}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2,
                      px: 2.5, py: 2, borderRadius: 2, cursor: 'pointer',
                      border: `1.5px solid ${selected ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)'}`,
                      bgcolor: selected ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)',
                      transition: 'all 0.15s ease',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.4)' },
                    }}
                  >
                    {String(project?.logoUrl || '').trim() ? (
                      <Box component="img" src={String(project.logoUrl)} alt={String(project.name || project.id)}
                        sx={{ width: 44, height: 44, borderRadius: 1.5, objectFit: 'contain', bgcolor: '#fff', p: 0.5, flexShrink: 0 }}
                      />
                    ) : (
                      <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <ApartmentOutlinedIcon sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 22 }} />
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15, color: '#fff', lineHeight: 1.2 }}>
                        {String(project.name || project.id)}
                      </Typography>
                    </Box>
                    {selected && (
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff', flexShrink: 0 }} />
                    )}
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Dialog>

      {/* ── Mode picker fullscreen dialog ──────────────────────────────────── */}
      <Dialog open={modePickerOpen} fullScreen PaperProps={{ sx: { background: 'linear-gradient(160deg, #0a1f5c 0%, #0d3080 50%, #0a1f5c 100%)', color: '#fff' } }}>
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', px: { xs: 2, sm: 4 }, py: 4 }}>

          {/* Hero */}
          <Box component="img" src={LOGO_URL} alt="Swarna Group"
            sx={{ width: { xs: 56, sm: 72 }, height: { xs: 56, sm: 72 }, objectFit: 'contain', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.12)', p: 1, mb: 2.5 }}
          />
          <Typography sx={{ fontFamily: FONT_HEADING, fontWeight: 700, fontSize: { xs: 22, sm: 28 }, color: '#fff', mb: 0.75 }}>
            Swarna Group
          </Typography>
          <Typography sx={{ fontFamily: FONT_BODY, fontSize: 13, color: 'rgba(255,255,255,0.55)', mb: { xs: 5, sm: 7 } }}>
            Choose which app to open
          </Typography>

          {/* Mode cards */}
          <Box sx={{ width: '100%', maxWidth: 480, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* CRM */}
            <Box
              onClick={() => { window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1'); localStorage.setItem(APP_MODE_STORAGE_KEY, 'crm'); setModePickerOpen(false); navigate('/', { replace: true }); }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 2.5, py: 2.25, borderRadius: 2, cursor: 'pointer',
                border: `1.5px solid ${appMode === 'crm' ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)'}`,
                bgcolor: appMode === 'crm' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)',
                transition: 'all 0.15s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.4)' },
              }}
            >
              <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AssessmentOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15, color: '#fff' }}>CRM</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>Leads, follow-ups & brokers</Typography>
              </Box>
              {appMode === 'crm' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff', flexShrink: 0 }} />}
            </Box>

            {/* Office */}
            <Box
              onClick={() => {
                window.localStorage.setItem(APP_MODE_PICKER_SKIP_ONCE_KEY, '1');
                localStorage.setItem(APP_MODE_STORAGE_KEY, 'office');
                const initialSelection = (currentProjectId && visibleProjects.some((p) => p.id === currentProjectId) ? currentProjectId : visibleProjects[0]?.id) || '';
                setProjectPickerSelection(String(initialSelection || ''));
                setProjectPickerOpen(true);
                setModePickerOpen(false);
              }}
              sx={{
                display: 'flex', alignItems: 'center', gap: 2,
                px: 2.5, py: 2.25, borderRadius: 2, cursor: 'pointer',
                border: `1.5px solid ${appMode === 'office' ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.15)'}`,
                bgcolor: appMode === 'office' ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.06)',
                transition: 'all 0.15s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.4)' },
              }}
            >
              <Box sx={{ width: 44, height: 44, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <ApartmentOutlinedIcon sx={{ color: '#fff', fontSize: 22 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontFamily: FONT_BODY, fontWeight: 700, fontSize: 15, color: '#fff' }}>Office</Typography>
                <Typography sx={{ fontFamily: FONT_BODY, fontSize: 12, color: 'rgba(255,255,255,0.5)', mt: 0.25 }}>Units, banks & reconciliation</Typography>
              </Box>
              {appMode === 'office' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff', flexShrink: 0 }} />}
            </Box>
          </Box>
        </Box>
      </Dialog>

    </Box>
  );
}
